import { Form, FormInstance, Input } from 'antd';
import { TFunction } from 'i18next';
import TextArea from 'antd/es/input/TextArea';
import DefaultButton from '../../../components/buttons/DefaultButton';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import SecondaryButton from '../../../components/buttons/SecondaryButton';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';
import { useEffect, useState } from 'react';
import { useWatch } from 'antd/es/form/Form';
import { REGEX_ALL_ALLOWED_SAFE_FILTER, REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER } from '../../../constants/regex';

interface EquipmentDescriptionProp {
  t: TFunction;
  form: FormInstance;
  disabled?: boolean;
  isViewMode?: boolean;
  handleCancel?: () => void;
  handleBack?: () => void;
  handleNext?: () => void;
}

const EquipmentDescription: React.FC<EquipmentDescriptionProp> = ({
  t,
  form,
  disabled,
  isViewMode = false,
  handleCancel,
  handleNext,
  handleBack,
}) => {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [enableNext, setEnableNext] = useState<boolean>(false);

  const equipmentDataFormValues = useWatch([], form);

  const toggleCancelModal = () => {
    isCancelModalOpen ? setIsCancelModalOpen(false) : setIsCancelModalOpen(true);
  };

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setEnableNext(true))
      .catch(() => setEnableNext(false));
  }, [form, equipmentDataFormValues]);

  return (
    <div className="m-0 w-full p-4">
      <div className="mb-4 space-y-2">
        <h2 className="border-b border-gray-200 pb-3 text-xl font-semibold text-gray-900">
          {t('equipmentDescriptionHeading')}
        </h2>
      </div>

      <div className="">
        {/* <div className="rounded-lg border border-gray-200 p-4 md:p-8"> */}
        <Form
          form={form}
          name="equipmentUsedFormData"
          layout="vertical"
          disabled={disabled || isViewMode}
          className={isViewMode ? 'view-mode-form' : ''}
        >
          {/* Brand */}
          <Form.Item
            label={t('brand')}
            name="equipmentBrand"
            validateFirst
            rules={[
              {
                required: true,
                message: t('brandRequired'),
              },
              {
                validator: (_, value) => {
                  if (value && value.trim() === '') {
                    return Promise.reject(t('brandSpaceValidation'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  // Allowing letters, numbers space comma dash forward slash fullstop and paranthesis               
                  const regex = REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER;
                  return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                },
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve();
                  }

                  const trimmedLength = value.replace(/\s/g, '').length;
                  if (trimmedLength < 3) {
                    return Promise.reject(t('brandMinValidation'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve();
                  }

                  const trimmedLength = value.replace(/\s/g, '').length;
                  if (trimmedLength > 100) {
                    return Promise.reject(t('branddMaxValidation'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder={t('brandPlaceholder')} />
          </Form.Item>

          {/* Maximum Capacity */}
          <Form.Item
            label={t('maximumCapacity')}
            name="equipmentMaxCapacity"
            validateFirst
            rules={[
              {
                required: true,
                message: t('maximumCapacityRequired'),
              },
              {
                validator: (_, value) => {
                  if (value && value.trim() === '') {
                    return Promise.reject(t('maximumCapacitySpaceValidation'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  // Allowing letters, numbers space comma dash forward slash fullstop and paranthesis               
                  const regex = REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER;
                  return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                },
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve();
                  }

                  const trimmedLength = value.replace(/\s/g, '').length;
                  if (trimmedLength < 1) {
                    return Promise.reject(t('maximumCapacityMinValidation'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve();
                  }

                  const trimmedLength = value.replace(/\s/g, '').length;
                  if (trimmedLength > 100) {
                    return Promise.reject(t('maximumCapacityMaxValidation'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder={t('maximumCapacityPlaceholder')} />
          </Form.Item>

          {/* Minimum Capacity */}
          <Form.Item
            label={t('minimumCapacity')}
            name="equipmentMinCapacity"
            validateFirst
            rules={[
              {
                required: true,
                message: t('minimumCapacityRequired'),
              },
              {
                validator: (_, value) => {
                  if (value && value.trim() === '') {
                    return Promise.reject(t('minimumCapacitySpaceValidation'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  // Allowing letters, numbers space comma dash forward slash fullstop and paranthesis               
                  const regex = REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER;
                  return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                },
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve();
                  }

                  const trimmedLength = value.replace(/\s/g, '').length;
                  if (trimmedLength < 1) {
                    return Promise.reject(t('minimumCapacityMinValidation'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve();
                  }

                  const trimmedLength = value.replace(/\s/g, '').length;
                  if (trimmedLength > 100) {
                    return Promise.reject(t('minimumCapacityMaxValidation'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder={t('minimumCapacityPlaceholder')} />
          </Form.Item>

          {/* Serial Number */}
          <Form.Item
            label={t('serialNumber')}
            name="equipmentSerialnumber"
            validateFirst
            rules={[
              {
                required: true,
                message: t('serialNumberRequired'),
              },
              {
                validator: (_, value) => {
                  if (value && value.trim() === '') {
                    return Promise.reject(t('serialNumberSpaceValidation'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  // Allow letters, numbers, space, punctuation and special characters              
                  const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                  return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                },
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve();
                  }

                  const trimmedLength = value.replace(/\s/g, '').length;
                  if (trimmedLength < 3) {
                    return Promise.reject(t('serialNumberMinValidation'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve();
                  }

                  const trimmedLength = value.replace(/\s/g, '').length;
                  if (trimmedLength > 100) {
                    return Promise.reject(t('serialNumberMaxValidation'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder={t('serialNumberPlaceholder')} />
          </Form.Item>

          {/* Equipment Used */}
          <Form.Item
            label={t('equipmentUsed')}
            name="equipmentName"
            validateFirst
            rules={[
              {
                required: true,
                message: t('equipmentUsedRequired'),
              },
              {
                validator: (_, value) => {
                  if (value && value.trim() === '') {
                    return Promise.reject(t('equipmentUsedSpaceValidation'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  // Allowing letters, numbers space comma dash forward slash fullstop and paranthesis               
                  const regex = REGEX_ALPHA_NUMERIC_MORE_SAFE_FILTER;
                  return regex.test(value) ? Promise.resolve() : Promise.reject(t('invalidInput'));
                },
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve();
                  }

                  const trimmedLength = value.replace(/\s/g, '').length;
                  if (trimmedLength < 3) {
                    return Promise.reject(t('equipmentUsedMinValidation'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.resolve();
                  }

                  const trimmedLength = value.replace(/\s/g, '').length;
                  if (trimmedLength > 100) {
                    return Promise.reject(t('equipmentUsedMaxValidation'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder={t('equipmentUsedPlaceholder')} />
          </Form.Item>

          {/* Observations */}
          {/*  <Form.Item
          label={t('observations')}
          name="observations"
          rules={[
            {
              required: true,
              message: t('observationsRequired'),
            },
          ]}
        >
          <TextArea placeholder={t('observationsPlaceholder')} rows={5} />
        </Form.Item> */}
        </Form>
      </div>

      {!isViewMode && (
        <div className="mt-6 flex w-full flex-col justify-end gap-4 pt-4 md:ml-auto md:w-1/2 md:flex-row">
          <DefaultButton handleOnClick={toggleCancelModal} width="100%">
            {t('cancel')}
          </DefaultButton>
          <SecondaryButton handleOnClick={() => handleBack?.()} width="100%">
            {t('back')}
          </SecondaryButton>
          {enableNext ? (
            <PrimaryButton
              handleOnClick={(e) => {
                e.preventDefault();
                handleNext?.();
              }}
              width="100%"
              disabled={disabled || isViewMode}
              hideBgGradient={true}
            >
              {t('next')}
            </PrimaryButton>
          ) : (
            <SecondaryButton
              handleOnClick={(e) => {
                e.preventDefault();
                handleNext?.();
              }}
              width="100%"
              disabled={!enableNext || isViewMode}
            >
              {t('next')}
            </SecondaryButton>
          )}
        </div>
      )}

      <ConfirmModelComponent
        isOpen={isCancelModalOpen}
        onClickedCancel={toggleCancelModal}
        header={t('verificationCancelModalTitle')}
        content={<p>{t('verificationCancelModalConfirmaiton')}</p>}
        footerAction={{
          confimation: {
            label: t('yes'),
            onClick: handleCancel ?? (() => {}),
          },
          cancel: {
            label: t('no'),
            onClick: toggleCancelModal,
          },
        }}
        styleClasses="z-100"
      />
    </div>
  );
};

export default EquipmentDescription;
